import { buildURL } from 'react-imgix';

import { isImgixUrl } from './isImgixUrl';

export { isImgixUrl };

/**
 * Air-hosted BYOS original CloudFront hosts.
 * Prod: byos-original-1.air.inc
 * Non-prod: byos-original-1-dev1.air.inc
 */
const BYOS_ORIGINAL_CF_HOST = /^byos-original(?:-\d+)?(?:-[a-z0-9]+)?\.air\.inc$/i;
/** Leftover per-pool Imgix hosts from the FOUND-826 TLD swap. Rewrite onto the shared proxy. */
const BYOS_ORIGINAL_IMGIX_HOST = /^byos-original(?:-\d+)?(?:-[a-z0-9]+)?\.imgix\.net$/i;
const BYOS_MEDIA_CF_HOST = /^byos-media(?:-\d+)?(?:-[a-z0-9]+)?\.air\.inc$/i;

export const DEFAULT_BYOS_IMGIX_PROXY_ROOT_URL = 'https://air-byos-proxy.imgix.net';

export type ClientImgixParams = Record<string, string | number | boolean | undefined | null>;

export type BuildImgixSignRequestUrlParams = {
  src: string;
  params?: ClientImgixParams;
  signPath: string;
  origin?: string;
};

const parseUrl = (src: string): URL | undefined => {
  try {
    return new URL(src);
  } catch {
    return undefined;
  }
};

const hostnameMatches = (src: string, pattern: RegExp): boolean => {
  const hostname = parseUrl(src)?.hostname;

  return hostname ? pattern.test(hostname) : false;
};

export const getByosImgixProxyRootUrl = (): string => {
  const fromEnv = process.env.NEXT_PUBLIC_IMGIX_BYOS_PROXY_ROOT_URL?.trim();
  const root = (fromEnv && fromEnv.length > 0 ? fromEnv : DEFAULT_BYOS_IMGIX_PROXY_ROOT_URL).replace(/\/$/, '');

  return root;
};

export const getByosImgixProxyHostname = (): string => {
  const parsed = parseUrl(getByosImgixProxyRootUrl());

  return parsed?.hostname.toLowerCase() ?? 'air-byos-proxy.imgix.net';
};

/**
 * CloudFront default domains (and any other pool hosts) listed in
 * `BYOS_CDN_POOL_HOSTS` while aliases are off. Comma-separated, server-only.
 * An unauthenticated sign BFF must not treat every `*.cloudfront.net` host as
 * an Air origin.
 */
const parseByosCdnPoolHosts = (): Set<string> => {
  const raw = process.env.BYOS_CDN_POOL_HOSTS;

  if (typeof raw !== 'string' || raw.trim().length === 0) {
    return new Set();
  }

  return new Set(
    raw.split(',').flatMap((part) => {
      const hostname = part
        .trim()
        .replace(/^https?:\/\//i, '')
        .replace(/\/$/, '')
        .split('/')[0]
        ?.toLowerCase();

      return hostname ? [hostname] : [];
    }),
  );
};

const wrapOriginInByosImgixProxy = (parsed: URL, { preserveSearch }: { preserveSearch?: boolean } = {}): string => {
  const originWithoutQuery = `https://${parsed.hostname}${parsed.pathname}`;
  const wrapped = `${getByosImgixProxyRootUrl()}/${encodeURIComponent(originWithoutQuery)}`;

  if (!preserveSearch) {
    return wrapped;
  }

  parsed.searchParams.delete('s');
  const search = parsed.searchParams.toString();

  return search ? `${wrapped}?${search}` : wrapped;
};

export const isSignedImgixUrl = (src: string): boolean => {
  if (!isImgixUrl(src)) {
    return false;
  }

  const parsed = parseUrl(src);

  return parsed?.searchParams.has('s') === true;
};

export const isByosOriginalCloudFrontUrl = (src: string): boolean => hostnameMatches(src, BYOS_ORIGINAL_CF_HOST);

export const isByosOriginalImgixUrl = (src: string): boolean => {
  const hostname = parseUrl(src)?.hostname.toLowerCase();

  return hostname === getByosImgixProxyHostname();
};

export const isByosMediaCloudFrontUrl = (src: string): boolean => hostnameMatches(src, BYOS_MEDIA_CF_HOST);

export const rewriteByosOriginalCloudFrontToImgix = (src: string): string | undefined => {
  const parsed = parseUrl(src);

  if (!parsed) {
    return undefined;
  }

  if (BYOS_ORIGINAL_CF_HOST.test(parsed.hostname)) {
    parsed.protocol = 'https:';
    return wrapOriginInByosImgixProxy(parsed);
  }

  if (BYOS_ORIGINAL_IMGIX_HOST.test(parsed.hostname)) {
    parsed.protocol = 'https:';
    parsed.hostname = parsed.hostname.replace(/\.imgix\.net$/i, '.air.inc');
    return wrapOriginInByosImgixProxy(parsed, { preserveSearch: true });
  }

  return undefined;
};

export const resolveByosImgixSrc = (src: string): string => {
  if (isByosOriginalImgixUrl(src)) {
    return src;
  }

  return rewriteByosOriginalCloudFrontToImgix(src) ?? src;
};

/**
 * True only for the shared BYOS Web Proxy host (`air-byos-proxy.imgix.net`, or
 * `NEXT_PUBLIC_IMGIX_BYOS_PROXY_ROOT_URL`) and leftover original CloudFront /
 * per-pool Imgix hosts that rewrite onto it. Air-managed Imgix (`air-*.imgix.net`,
 * edits, avatars, …) must not go through the sign BFF.
 */
export const needsImgixServerSign = (src: string): boolean => isByosOriginalImgixUrl(resolveByosImgixSrc(src));

export const getImgixSignPath = (): string | undefined => {
  const path = process.env.NEXT_PUBLIC_IMGIX_SIGN_PATH;

  return path && path.length > 0 ? path : undefined;
};

/** Dummy origin so relative `/api/imgix/sign?...` paths parse in Node and the browser. */
const SIGN_REQUEST_PARSE_ORIGIN = 'https://app.air.inc';

export const isImgixSignRequestUrl = (src: string): boolean => {
  const signPath = getImgixSignPath();

  if (!signPath) {
    return false;
  }

  try {
    return new URL(src, SIGN_REQUEST_PARSE_ORIGIN).pathname === signPath;
  } catch {
    return false;
  }
};

export const getByosImgixProxyOriginUrl = (src: string): string | undefined => {
  const parsed = parseUrl(resolveByosImgixSrc(src));

  if (parsed?.protocol !== 'https:') {
    return undefined;
  }

  if (!isByosOriginalImgixUrl(parsed.href)) {
    return undefined;
  }

  let originUrl: string;
  try {
    originUrl = decodeURIComponent(parsed.pathname.replace(/^\//, ''));
  } catch {
    return undefined;
  }

  const originHostname = parseUrl(originUrl)?.hostname.toLowerCase();

  if (!originHostname) {
    return undefined;
  }

  return BYOS_ORIGINAL_CF_HOST.test(originHostname) ||
    BYOS_MEDIA_CF_HOST.test(originHostname) ||
    parseByosCdnPoolHosts().has(originHostname)
    ? originUrl
    : undefined;
};

const compactParams = (params: ClientImgixParams | undefined): Record<string, string> => {
  if (!params) {
    return {};
  }

  return Object.fromEntries(
    Object.entries(params).flatMap(([key, value]) => {
      if (value === undefined || value === null || value === '') {
        return [];
      }

      return [[key, String(value)]];
    }),
  );
};

export const buildImgixSignRequestUrl = ({ src, params, signPath, origin }: BuildImgixSignRequestUrlParams): string => {
  const search = new URLSearchParams();
  search.set('src', src);
  Object.entries(compactParams(params)).forEach(([key, value]) => {
    if (key !== 'src' && key !== 's') {
      search.set(key, value);
    }
  });

  const path = `${signPath}?${search.toString()}`;

  if (!origin) {
    return path;
  }

  return `${origin.replace(/\/$/, '')}${path.startsWith('/') ? path : `/${path}`}`;
};

type BuildClientImgixUrlOptions = {
  disableLibraryParam?: boolean;
  disableSrcSet?: boolean;
  src?: string;
};

const appendImgixParams = (src: string, params: Record<string, string>): string => {
  const parsed = parseUrl(src);

  if (!parsed) {
    return src;
  }

  Object.entries(params).forEach(([key, value]) => {
    if (key !== 'src' && key !== 's') {
      parsed.searchParams.set(key, value);
    }
  });

  return parsed.toString();
};

export const buildClientImgixUrl = (
  src: string,
  params?: ClientImgixParams,
  options?: BuildClientImgixUrlOptions,
): string => {
  const resolvedSrc = resolveByosImgixSrc(src);
  const signPath = getImgixSignPath();
  const compact = compactParams(params);

  if (needsImgixServerSign(resolvedSrc)) {
    if (signPath) {
      return buildImgixSignRequestUrl({ params, signPath, src: resolvedSrc });
    }

    if (isSignedImgixUrl(resolvedSrc)) {
      return resolvedSrc;
    }

    return appendImgixParams(resolvedSrc, compact);
  }

  if (isSignedImgixUrl(resolvedSrc)) {
    return resolvedSrc;
  }

  if (!isImgixUrl(resolvedSrc)) {
    return resolvedSrc;
  }

  return buildURL(resolvedSrc, compact, {
    disableLibraryParam: true,
    src: resolvedSrc,
    ...options,
  });
};

/**
 * Pass a URL through the Imgix sign BFF when it is the BYOS proxy distribution.
 * Other Imgix hosts are returned unchanged — including already-public Air-managed URLs.
 */
export const withByosImgixServerSign = (src: string, params?: ClientImgixParams): string =>
  needsImgixServerSign(src) ? buildClientImgixUrl(src, params) : src;
